import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { EChartsOption } from 'echarts';
import type { GolfCanadaMemberDto, GolfScoreDto, GolfScoresDto } from '@jordan/types';
import { Alert, Breadcrumb, Button, EChart, PageHeader, Select, Spinner } from '@jordan/ui';
import { AppChrome } from '../../../components/AppChrome';
import { ApiError, apiDelete, apiGet, apiPost, apiPut } from '../../../lib/api';
import { golfSidebar } from './golfSidebar';

const shortDate = (ymd: string) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const longDate = (ymd: string) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
const fmt1 = (n: number | null) => (n == null ? '—' : n.toFixed(1));
const pct = (n: number | null, d = 0) => (n == null ? '—' : `${(n * 100).toFixed(d)}%`);
// Mirrors the Golf Canada score display: gross score, "(n)" scaled holes, and "#" when edited.
const scoreLabel = (r: GolfScoreDto) =>
  `${r.score}${r.scaledHolesPlayed ? ` (${r.scaledHolesPlayed})` : ''}${r.isEdited ? ' #' : ''}`;

// ---- Selectable trend metrics ----
interface Metric {
  key: string;
  label: string;
  /** Raw per-round value (percentages are 0–1 fractions). null when not recorded. */
  get: (r: GolfScoreDto) => number | null;
  /** true → value is a 0–1 fraction shown as a percentage. */
  percent?: boolean;
  decimals: number;
  /** Restrict to complete 18-hole rounds (used for gross score). */
  eighteenOnly?: boolean;
}

const METRICS: Metric[] = [
  { key: 'score', label: 'Score', get: (r) => r.score, decimals: 0, eighteenOnly: true },
  { key: 'handicap', label: 'Handicap Factor', get: (r) => r.handicapFactor, decimals: 1 },
  { key: 'differential', label: 'Differential', get: (r) => r.differential, decimals: 1 },
  { key: 'adjDifferential', label: 'Adjusted Differential', get: (r) => r.adjustedDifferential, decimals: 1 },
  { key: 'birdies', label: 'Birdies / Round', get: (r) => r.birdies, decimals: 0 },
  { key: 'scrambling', label: 'Scrambling (%)', get: (r) => r.scrambling, percent: true, decimals: 0 },
  { key: 'sandSave', label: 'Sand Saves (%)', get: (r) => r.sandSave, percent: true, decimals: 0 },
  { key: 'bounceBack', label: 'Bounce Back (%)', get: (r) => r.bounceBack, percent: true, decimals: 0 },
  { key: 'par3', label: 'Par 3 Average', get: (r) => r.par3Avg, decimals: 1 },
  { key: 'par4', label: 'Par 4 Average', get: (r) => r.par4Avg, decimals: 1 },
  { key: 'par5', label: 'Par 5 Average', get: (r) => r.par5Avg, decimals: 1 },
];

const metricValue = (r: GolfScoreDto, m: Metric): number | null => {
  const v = m.get(r);
  if (v == null) return null;
  return m.percent ? v * 100 : v;
};
const fmtMetric = (v: number | null, m: Metric): string =>
  v == null ? '—' : m.percent ? `${v.toFixed(m.decimals)}%` : v.toFixed(m.decimals);

/** Rounds (newest-first) that carry a value for this metric, honouring the 18-hole restriction. */
const roundsForMetric = (rounds: GolfScoreDto[], m: Metric): GolfScoreDto[] =>
  rounds.filter((r) => (!m.eighteenOnly || r.holes === '18') && m.get(r) != null);

function trendOption(rounds: GolfScoreDto[], m: Metric): EChartsOption {
  const series = roundsForMetric(rounds, m).slice().reverse(); // chronological
  return {
    textStyle: { fontFamily: 'var(--body-font-family)', color: 'var(--body-color)' },
    grid: { left: 8, right: 16, top: 24, bottom: 24, containLabel: true },
    tooltip: {
      trigger: 'axis',
      formatter: (p: unknown) => {
        const arr = p as { dataIndex: number }[];
        const r = series[arr[0].dataIndex];
        return `${longDate(r.date)}<br/><b>${fmtMetric(metricValue(r, m), m)}</b> · ${r.course ?? r.tee ?? '—'}`;
      },
    },
    xAxis: {
      type: 'category',
      data: series.map((r) => shortDate(r.date)),
      axisLabel: { color: 'rgba(var(--body-color-rgb), .65)' },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      scale: true,
      axisLabel: {
        color: 'rgba(var(--body-color-rgb), .55)',
        formatter: (v: number) => (m.percent ? `${v}%` : `${v}`),
      },
      splitLine: { lineStyle: { color: 'var(--gray-200)', type: 'dashed' } },
    },
    series: [
      {
        name: m.label,
        type: 'line',
        smooth: true,
        data: series.map((r) => metricValue(r, m)),
        itemStyle: { color: 'var(--bs-primary)' },
        areaStyle: { color: 'rgba(12,131,255,0.12)' },
      },
    ],
  } as EChartsOption;
}

// Palette for the distribution donut segments (green → red across the range of a metric's bins).
const DIST_COLORS = ['#10b981', '#0ea5e9', '#6366f1', '#f59e0b', '#ef4444', '#a855f7'];

/** Auto-binned distribution of the selected metric, rendered as a coloured donut. */
function distributionOption(rounds: GolfScoreDto[], m: Metric): EChartsOption {
  const values = roundsForMetric(rounds, m).map((r) => metricValue(r, m) as number);
  const bins: { label: string; count: number }[] = [];
  if (values.length) {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min;
    const binCount = Math.min(6, Math.max(1, new Set(values).size));
    const width = span === 0 ? 1 : span / binCount;
    for (let i = 0; i < binCount; i += 1) {
      const lo = min + i * width;
      const hi = i === binCount - 1 ? max : lo + width;
      const count = values.filter((v) => (i === binCount - 1 ? v >= lo && v <= hi : v >= lo && v < hi)).length;
      const fmt = (n: number) => (m.percent ? `${n.toFixed(0)}%` : n.toFixed(m.decimals));
      bins.push({ label: span === 0 ? fmt(min) : `${fmt(lo)}–${fmt(hi)}`, count });
    }
  }
  const data = bins
    .map((b, i) => ({ name: b.label, value: b.count, itemStyle: { color: DIST_COLORS[i % DIST_COLORS.length] } }))
    .filter((d) => d.value > 0);
  return {
    textStyle: { fontFamily: 'var(--body-font-family)', color: 'var(--body-color)' },
    tooltip: { trigger: 'item', formatter: '{b}: {c} rounds ({d}%)' },
    legend: { bottom: 0, textStyle: { color: 'var(--body-color)' } },
    series: [
      {
        type: 'pie',
        radius: ['55%', '78%'],
        center: ['50%', '44%'],
        avoidLabelOverlap: true,
        label: { show: false },
        data,
      },
    ],
  } as EChartsOption;
}

// ---- Golf Canada credentials form (used when linking, and when a stored session has lapsed) ----
function SignInForm({ onSignedIn, autoFocus = false }: { onSignedIn: () => void; autoFocus?: boolean }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [signInErr, setSignInErr] = useState<string | null>(null);

  const signIn = useCallback(async () => {
    if (!email.trim() || !password) return;
    setSigningIn(true);
    setSignInErr(null);
    try {
      await apiPost('/api/health/golf/golfcanada/signin', { email: email.trim(), password });
      onSignedIn();
    } catch (e) {
      setSignInErr(
        e instanceof ApiError && e.status === 401
          ? 'Incorrect Golf Canada email or password.'
          : 'Could not sign in to Golf Canada. Please try again.',
      );
      setSigningIn(false);
    }
  }, [email, password, onSignedIn]);

  return (
    <>
      {signInErr && (
        <Alert variant="danger" icon="ph-warning" dismissible>
          {signInErr}
        </Alert>
      )}

      <form
        className="row g-2 align-items-end"
        onSubmit={(e) => {
          e.preventDefault();
          void signIn();
        }}
        style={{ maxWidth: 560 }}
      >
        <div className="col-sm">
          <label className="form-label fs-sm text-muted mb-1">Golf Canada username or email</label>
          <input
            type="text"
            className="form-control"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus={autoFocus}
          />
        </div>
        <div className="col-sm">
          <label className="form-label fs-sm text-muted mb-1">Password</label>
          <input
            type="password"
            className="form-control"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="col-sm-auto">
          <Button type="submit" variant="primary" icon="ph-sign-in" disabled={signingIn || !email.trim() || !password}>
            {signingIn ? 'Signing in…' : 'Sign in'}
          </Button>
        </div>
      </form>
    </>
  );
}

/**
 * Shown alongside the scores when they came back from a public read — either a search-only link or a
 * signed-in one whose session lapsed. Signing in restores course names and Game Tracker stats.
 */
function SignInPrompt({ onSignedIn }: { onSignedIn: () => void }) {
  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body">
        <h6 className="mb-1">
          <i className="ph-lock-simple-open me-1" />
          Sign in for course names and detailed stats
        </h6>
        <p className="text-muted fs-sm mb-3">
          These scores came from your public Golf Canada profile. Sign in to the Score Centre to add course names,
          scorecards and Game Tracker statistics.
        </p>
        <SignInForm onSignedIn={onSignedIn} />
      </div>
    </div>
  );
}

// ---- Not-linked state: sign in (primary) or search by name (public fallback) ----
function LinkPanel({ onLinked }: { onLinked: () => void }) {
  const [text, setText] = useState('');
  const [results, setResults] = useState<GolfCanadaMemberDto[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const search = useCallback(async () => {
    const q = text.trim();
    if (q.length < 2) return;
    setSearching(true);
    setErr(null);
    try {
      const { members } = await apiGet<{ members: GolfCanadaMemberDto[] }>(
        `/api/health/golf/golfcanada/search?text=${encodeURIComponent(q)}`,
      );
      setResults(members);
    } catch {
      setErr('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  }, [text]);

  const link = useCallback(
    async (individualId: number) => {
      setLinkingId(individualId);
      setErr(null);
      try {
        await apiPut('/api/health/golf/golfcanada/link', { individualId });
        onLinked();
      } catch {
        setErr('Could not link that golfer. Please try again.');
        setLinkingId(null);
      }
    },
    [onLinked],
  );

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h5 className="mb-1">Connect your Golf Canada account</h5>
        <p className="text-muted">
          Sign in to the Golf Canada Score Centre to pull in your handicap, scoring history and course names. Your
          password is exchanged for a secure token and <span className="fw-semibold">never stored</span>.
        </p>

        <SignInForm onSignedIn={onLinked} autoFocus />

        <hr className="my-4" />

        <h6 className="mb-1">Or find me by name</h6>
        <p className="text-muted fs-sm">
          Links to your public profile only — handicap and scores, but <span className="fw-semibold">no course names</span>{' '}
          or detailed stats. Search by <span className="fw-semibold">last name</span> (e.g. “Baxter, Mike”).
        </p>

        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        <form
          className="d-flex gap-2 mb-3"
          onSubmit={(e) => {
            e.preventDefault();
            void search();
          }}
          style={{ maxWidth: 480 }}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Search your name…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Button type="submit" variant="primary" icon="ph-magnifying-glass" disabled={searching || text.trim().length < 2}>
            Search
          </Button>
        </form>

        {searching && <Spinner />}

        {results && !searching && (
          <div className="table-responsive">
            {results.length === 0 ? (
              <p className="text-muted mb-0">No golfers found. Try “Last, First”.</p>
            ) : (
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Club</th>
                    <th>Region</th>
                    <th className="text-end">Handicap</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {results.map((m) => (
                    <tr key={m.individualId}>
                      <td className="fw-semibold">{m.name}</td>
                      <td>{m.club ?? '—'}</td>
                      <td>{m.region ?? '—'}</td>
                      <td className="text-end">{m.handicap ?? '—'}</td>
                      <td className="text-end">
                        <Button
                          variant="light"
                          size="sm"
                          icon="ph-link"
                          disabled={linkingId != null}
                          onClick={() => void link(m.individualId)}
                        >
                          {linkingId === m.individualId ? 'Linking…' : 'This is me'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ label, value, hint, icon }: { label: string; value: string; hint?: string; icon: string }) => (
  <div className="col-md-3 col-6">
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex align-items-center text-muted mb-1">
          <i className={`${icon} me-2`} />
          {label}
        </div>
        <h3 className="fw-bold mb-0">{value}</h3>
        {hint && <div className="text-muted fs-sm">{hint}</div>}
      </div>
    </div>
  </div>
);

export function GolfScoresPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [data, setData] = useState<GolfScoresDto | null>(null);
  const [linked, setLinked] = useState<boolean | null>(null); // null = unknown/loading
  const [err, setErr] = useState<string | null>(null);
  const [metricKey, setMetricKey] = useState('score');

  const load = useCallback(() => {
    setErr(null);
    setLinked(null);
    apiGet<GolfScoresDto>('/api/health/golf/golfcanada/scores')
      .then((d) => {
        setData(d);
        setLinked(true);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 409) {
          setLinked(false);
        } else {
          setErr('Could not load your Golf Canada scores.');
          setLinked(false);
        }
      });
  }, []);
  useEffect(load, [load]);

  const unlink = useCallback(async () => {
    try {
      await apiDelete('/api/health/golf/golfcanada/link');
      setData(null);
      setLinked(false);
    } catch {
      setErr('Could not unlink your account.');
    }
  }, []);

  // Only offer metrics that at least one round actually has data for.
  const availableMetrics = useMemo(
    () => (data ? METRICS.filter((m) => data.rounds.some((r) => m.get(r) != null)) : []),
    [data],
  );
  const metric = availableMetrics.find((m) => m.key === metricKey) ?? availableMetrics[0] ?? METRICS[0];
  const trend = useMemo(() => (data ? trendOption(data.rounds, metric) : null), [data, metric]);
  const dist = useMemo(() => (data ? distributionOption(data.rounds, metric) : null), [data, metric]);

  return (
    <AppChrome secondarySidebar={golfSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Golf - <span className="fw-normal">Scores</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Golf' }, { label: 'Scores' }]} />}
      />

      <div className="content">
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {linked === null && <Spinner />}

        {/* Rendered on load failures too (with the alert above it), so signing in again is always
            reachable — an unrecoverable error otherwise leaves the page with no way forward. */}
        {linked === false && <LinkPanel onLinked={load} />}

        {linked && data && !data.authenticated && <SignInPrompt onSignedIn={load} />}

        {linked && data && (
          <>
            {/* Profile / source line */}
            <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
              <div>
                <span className="fw-semibold">{data.profile.name ?? 'Golfer'}</span>
                {data.profile.club && <span className="text-muted"> · {data.profile.club}</span>}
                {data.profile.region && <span className="text-muted"> · {data.profile.region}</span>}
              </div>
              <span className="badge bg-success bg-opacity-10 text-success">
                <i className="ph-link me-1" />
                Golf Canada
              </span>
              <Button variant="light" size="sm" icon="ph-link-break" className="ms-auto" onClick={() => void unlink()}>
                Unlink
              </Button>
            </div>

            {/* Headline stats */}
            <div className="row g-3 mb-3">
              <StatCard label="Handicap factor" value={data.profile.handicap ?? '—'} hint="current" icon="ph-gauge" />
              <StatCard label="Low handicap" value={data.profile.lowHandicap ?? '—'} icon="ph-trend-down" />
              <StatCard
                label="Rounds played"
                value={String(data.stats.rounds)}
                hint={`${data.stats.roundsThisYear} this year`}
                icon="ph-stack"
              />
              <StatCard
                label="Scoring average"
                value={data.stats.scoringAverage != null ? data.stats.scoringAverage.toFixed(1) : '—'}
                hint="18 holes"
                icon="ph-chart-line"
              />
            </div>

            {/* Charts */}
            <div className="row g-3 mb-3">
              <div className="col-xl-8">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent d-flex align-items-center gap-2">
                    <h6 className="mb-0 fw-semibold me-auto">{metric.label} trend</h6>
                    <Select
                      selectSize="sm"
                      value={metric.key}
                      onChange={(e) => setMetricKey(e.target.value)}
                      options={availableMetrics.map((m) => ({ value: m.key, label: m.label }))}
                      style={{ width: 'auto' }}
                      aria-label="Trend metric"
                    />
                  </div>
                  <div className="card-body">
                    {trend && <EChart option={trend} height={300} />}
                  </div>
                </div>
              </div>
              <div className="col-xl-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-transparent">
                    <h6 className="mb-0 fw-semibold">{metric.label} distribution</h6>
                  </div>
                  <div className="card-body">
                    {dist && <EChart option={dist} height={300} />}
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary stats */}
            <div className="row g-3 mb-3">
              <StatCard
                label="Best round"
                value={data.stats.best ? String(data.stats.best.score) : '—'}
                hint={data.stats.best ? longDate(data.stats.best.date) : undefined}
                icon="ph-trophy"
              />
              <StatCard
                label="Avg differential"
                value={data.stats.avgDifferential != null ? data.stats.avgDifferential.toFixed(1) : '—'}
                icon="ph-function"
              />
              <StatCard
                label="Recent form"
                value={data.stats.recentDifferential != null ? data.stats.recentDifferential.toFixed(1) : '—'}
                hint="avg diff, last 5"
                icon="ph-pulse"
              />
              <StatCard label="Rounds this year" value={String(data.stats.roundsThisYear)} icon="ph-calendar-check" />
            </div>

            {/* Game Tracker aggregates (signed-in only) */}
            {data.summary && (
              <div className="row g-3 mb-3">
                <StatCard label="Putts / round" value={fmt1(data.summary.puttsPerRound)} icon="ph-arrow-clockwise" />
                <StatCard label="Greens in regulation" value={pct(data.summary.girPercent)} icon="ph-target" />
                <StatCard label="Fairways hit" value={pct(data.summary.firPercent)} icon="ph-path" />
                <StatCard label="Scrambling" value={pct(data.summary.scramblingPercent)} icon="ph-arrows-clockwise" />
              </div>
            )}

            {/* Recent rounds */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent">
                <h6 className="mb-0 fw-semibold">Recent rounds</h6>
              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Course</th>
                      <th className="text-center">Holes</th>
                      <th>Tees</th>
                      <th className="text-end">CR/SL</th>
                      <th className="text-end">Score</th>
                      <th className="text-end">Diff</th>
                      <th className="text-end">Adj</th>
                      <th className="text-end">Adj Diff</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.rounds.slice(0, 20).map((r) => (
                      <tr key={r.id}>
                        <td>{r.date}</td>
                        <td>{r.course ?? '—'}</td>
                        <td className="text-center">{r.holes}</td>
                        <td>{r.tee ?? '—'}</td>
                        <td className="text-end">
                          {r.rating != null && r.slope != null ? `${r.rating.toFixed(1)} / ${r.slope}` : '—'}
                        </td>
                        <td className="text-end fw-semibold">{scoreLabel(r)}</td>
                        <td className="text-end text-danger">{fmt1(r.differential)}</td>
                        <td className="text-end">{fmt1(r.adjustment)}</td>
                        <td className="text-end">{fmt1(r.adjustedDifferential)}</td>
                        <td className="text-end">
                          <a
                            href={`/health/golf/scores/${r.id}`}
                            className="text-body-secondary"
                            title="View round details"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/health/golf/scores/${r.id}`);
                            }}
                          >
                            <i className="ph-chart-bar" />
                          </a>
                        </td>
                      </tr>
                    ))}
                    {data.rounds.length === 0 && (
                      <tr>
                        <td colSpan={10} className="text-center text-muted py-4">
                          No rounds posted yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-muted fs-sm mt-3">
              <i className="ph-info me-1" />
              Sourced from your Golf Canada Score Centre. Per-round fairway, greens-in-regulation and putting trends
              aren’t shown because Golf Canada’s detailed-stat endpoints are currently returning errors; their
              season totals appear in the cards above{data.summary ? '' : ' once you sign in'}.
            </p>
          </>
        )}
      </div>
    </AppChrome>
  );
}
